import React, { useMemo } from 'react';

import { capitalizeTopic } from '@utils/formatting';

import MyTIReadingStat from './ReadingStat';
import MyTISection from './Section';
import { MY_TI_ENGAGEMENT_LABELS, MY_TI_SECTION_IDS } from './constants';
import type { MyTIReadingStatsProps } from './interfaces';

const MyTIReadingStats = ({
  articlesRead,
  minutesRead,
  mostReadTopic,
  mostReadNewsletters,
  newsletterNames,
}: MyTIReadingStatsProps) => {
  /**
   * Data transformation to get the most read newsletter
   * Data comes back in the form of ['newsletter_id: count', ...], so
   * we need to get the largest count and that newsletter's name
   */
  const mostReadNewsletter = useMemo(() => {
    return mostReadNewsletters?.reduce(
      (acc, pair) => {
        const [newsletterId, count] = pair.split(': ');

        if (acc.count < Number(count)) {
          acc.count = Number(count);
          acc.newsletter = newsletterNames?.[newsletterId] || '';
        }

        return acc;
      },
      { count: 0, newsletter: '' }
    );
  }, [mostReadNewsletters, newsletterNames]);

  const capitalizedTopic = useMemo(() => capitalizeTopic(mostReadTopic || ''), [mostReadTopic]);

  if (!articlesRead && !minutesRead && !mostReadTopic && !mostReadNewsletter?.newsletter) {
    return null;
  }

  return (
    <MyTISection
      id={MY_TI_SECTION_IDS.ReadingStats}
      highlight={MY_TI_ENGAGEMENT_LABELS.Subscribed.ReadingStats.label}
      subtitle={MY_TI_ENGAGEMENT_LABELS.Subscribed.ReadingStats.subtitle}
      title={MY_TI_ENGAGEMENT_LABELS.Subscribed.ReadingStats.title}
      disableBorder
    >
      <div className="items-between relative mt-48 flex flex-col justify-center gap-32 sm:flex-row sm:flex-wrap md:flex-nowrap lg:gap-64">
        {articlesRead && <MyTIReadingStat index={0} name="Articles Read" value={articlesRead} />}
        {minutesRead && (
          <MyTIReadingStat index={1} name="Time Spent" value={`${minutesRead} mins`} />
        )}
        {mostReadTopic && (
          <MyTIReadingStat index={2} name="Top Topic" value={capitalizedTopic} small />
        )}
        {mostReadNewsletter?.newsletter && (
          <MyTIReadingStat
            index={3}
            name="Most-Read Newsletter"
            value={mostReadNewsletter.newsletter}
            small
          />
        )}
      </div>
    </MyTISection>
  );
};

export default MyTIReadingStats;
